import { createFeature, createReducer, on } from "@ngrx/store";
import {
    ManagedWorkflow,
    WorkflowMutationState,
    WorkflowSection,
    WorkflowStep,
} from "../models/workflow.models";
import { WorkflowsActions } from "./workflows.actions";

export interface WorkflowsState {
    workflows: ManagedWorkflow[];
    selectedWorkflowId: string | null;
    steps: WorkflowStep[];
    workflowQuery: string;
    stepQuery: string;
    section: WorkflowSection;
    previewIndex: number;
    loadingWorkflows: boolean;
    loadingSteps: boolean;
    loaded: boolean;
    error: boolean;
    mutation: WorkflowMutationState | null;
}

const initialState: WorkflowsState = {
    workflows: [],
    selectedWorkflowId: null,
    steps: [],
    workflowQuery: "",
    stepQuery: "",
    section: "steps",
    previewIndex: 0,
    loadingWorkflows: false,
    loadingSteps: false,
    loaded: false,
    error: false,
    mutation: null,
};

function replaceWorkflow(
    workflows: ManagedWorkflow[],
    workflow: ManagedWorkflow
): ManagedWorkflow[] {
    return workflows
        .map((current) =>
            current.gmvid === workflow.gmvid ? workflow : current
        )
        .sort((a, b) => a.title.localeCompare(b.title));
}

export const workflowsFeature = createFeature({
    name: "workflows",
    reducer: createReducer(
        initialState,
        on(
            WorkflowsActions.pageOpened,
            (state): WorkflowsState => ({
                ...state,
                loadingWorkflows: true,
                error: false,
            })
        ),
        on(
            WorkflowsActions.workflowsLoaded,
            (state, { workflows }): WorkflowsState => ({
                ...state,
                workflows,
                selectedWorkflowId: workflows.some(
                    (workflow) => workflow.gmvid === state.selectedWorkflowId
                )
                    ? state.selectedWorkflowId
                    : (workflows[0]?.gmvid ?? null),
                loadingWorkflows: false,
                loaded: true,
                error: false,
            })
        ),
        on(
            WorkflowsActions.workflowsLoadFailed,
            (state): WorkflowsState => ({
                ...state,
                loadingWorkflows: false,
                loaded: true,
                error: true,
            })
        ),
        on(
            WorkflowsActions.workflowSelected,
            (state, { workflowId }): WorkflowsState => ({
                ...state,
                selectedWorkflowId: workflowId,
                steps: [],
                stepQuery: "",
                previewIndex: 0,
                loadingSteps: true,
                error: false,
            })
        ),
        on(
            WorkflowsActions.stepsLoaded,
            (state, { workflowId, steps }): WorkflowsState =>
                state.selectedWorkflowId === workflowId
                    ? {
                          ...state,
                          steps,
                          loadingSteps: false,
                          error: false,
                      }
                    : state
        ),
        on(
            WorkflowsActions.stepsLoadFailed,
            (state, { workflowId }): WorkflowsState =>
                state.selectedWorkflowId === workflowId
                    ? { ...state, loadingSteps: false, error: true }
                    : state
        ),
        on(
            WorkflowsActions.workflowQueryChanged,
            (state, { query }): WorkflowsState => ({
                ...state,
                workflowQuery: query,
            })
        ),
        on(
            WorkflowsActions.stepQueryChanged,
            (state, { query }): WorkflowsState => ({
                ...state,
                stepQuery: query,
            })
        ),
        on(
            WorkflowsActions.sectionChanged,
            (state, { section }): WorkflowsState => ({
                ...state,
                section,
                previewIndex: section === "preview" ? state.previewIndex : 0,
            })
        ),
        on(
            WorkflowsActions.previewIndexChanged,
            (state, { index }): WorkflowsState => ({
                ...state,
                previewIndex: Math.max(
                    0,
                    Math.min(index, state.steps.length - 1)
                ),
            })
        ),
        on(
            WorkflowsActions.createRequested,
            (state): WorkflowsState => ({
                ...state,
                mutation: { type: "create", status: "pending" },
            })
        ),
        on(
            WorkflowsActions.createSucceeded,
            (state, { workflow }): WorkflowsState => ({
                ...state,
                workflows: [...state.workflows, workflow].sort((a, b) =>
                    a.title.localeCompare(b.title)
                ),
                selectedWorkflowId: workflow.gmvid,
                steps: [],
                section: "steps",
                mutation: { type: "create", status: "success" },
            })
        ),
        on(
            WorkflowsActions.renameRequested,
            (state): WorkflowsState => ({
                ...state,
                mutation: { type: "rename", status: "pending" },
            })
        ),
        on(
            WorkflowsActions.renameSucceeded,
            (state, { workflow }): WorkflowsState => ({
                ...state,
                workflows: replaceWorkflow(state.workflows, workflow),
                mutation: { type: "rename", status: "success" },
            })
        ),
        on(
            WorkflowsActions.ownerUpdateRequested,
            (state): WorkflowsState => ({
                ...state,
                mutation: { type: "update-owner", status: "pending" },
            })
        ),
        on(
            WorkflowsActions.ownerUpdateSucceeded,
            (state, { workflow }): WorkflowsState => ({
                ...state,
                workflows: replaceWorkflow(state.workflows, workflow),
                mutation: { type: "update-owner", status: "success" },
            })
        ),
        on(
            WorkflowsActions.deleteRequested,
            (state): WorkflowsState => ({
                ...state,
                mutation: { type: "delete", status: "pending" },
            })
        ),
        on(
            WorkflowsActions.deleteSucceeded,
            (state, { workflowId }): WorkflowsState => {
                const workflows = state.workflows.filter(
                    (workflow) => workflow.gmvid !== workflowId
                );
                return {
                    ...state,
                    workflows,
                    selectedWorkflowId: workflows[0]?.gmvid ?? null,
                    steps: [],
                    mutation: { type: "delete", status: "success" },
                };
            }
        ),
        on(
            WorkflowsActions.createStepRequested,
            (state): WorkflowsState => ({
                ...state,
                mutation: { type: "create-step", status: "pending" },
            })
        ),
        on(
            WorkflowsActions.createStepSucceeded,
            (state, { workflow, step }): WorkflowsState => ({
                ...state,
                workflows: replaceWorkflow(state.workflows, workflow),
                steps: [...state.steps, step],
                mutation: { type: "create-step", status: "success" },
            })
        ),
        on(
            WorkflowsActions.updateStepRequested,
            (state): WorkflowsState => ({
                ...state,
                mutation: { type: "update-step", status: "pending" },
            })
        ),
        on(
            WorkflowsActions.updateStepSucceeded,
            (state, { workflow, step, originalStepId }): WorkflowsState => ({
                ...state,
                workflows: replaceWorkflow(state.workflows, workflow),
                steps: state.steps.map((current) =>
                    current.gmvid === originalStepId ? step : current
                ),
                mutation: { type: "update-step", status: "success" },
            })
        ),
        on(
            WorkflowsActions.deleteStepRequested,
            (state): WorkflowsState => ({
                ...state,
                mutation: { type: "delete-step", status: "pending" },
            })
        ),
        on(
            WorkflowsActions.deleteStepSucceeded,
            (state, { workflow, stepId }): WorkflowsState => ({
                ...state,
                workflows: replaceWorkflow(state.workflows, workflow),
                steps: state.steps.filter((step) => step.gmvid !== stepId),
                previewIndex: 0,
                mutation: { type: "delete-step", status: "success" },
            })
        ),
        on(
            WorkflowsActions.reorderStepsRequested,
            (state): WorkflowsState => ({
                ...state,
                mutation: { type: "reorder-steps", status: "pending" },
            })
        ),
        on(
            WorkflowsActions.reorderStepsSucceeded,
            (state, { workflow }): WorkflowsState => {
                const byId = new Map(
                    state.steps.map((step) => [step.gmvid, step])
                );
                return {
                    ...state,
                    workflows: replaceWorkflow(state.workflows, workflow),
                    steps: workflow.steps
                        .map((stepId) => byId.get(stepId))
                        .filter((step): step is WorkflowStep => Boolean(step)),
                    mutation: { type: "reorder-steps", status: "success" },
                };
            }
        ),
        on(
            WorkflowsActions.mutationFailed,
            (state): WorkflowsState => ({
                ...state,
                mutation: state.mutation
                    ? { ...state.mutation, status: "error" }
                    : null,
            })
        ),
        on(
            WorkflowsActions.mutationStatusCleared,
            (state): WorkflowsState => ({ ...state, mutation: null })
        )
    ),
});
